import { useState, useEffect } from 'react'
import type { User } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { supabase } from '../../utils/supabase'
import { posthog } from '../../utils/posthog'

/** Returns true when the two user snapshots are semantically identical. */
function isSameUser(a: User | null, b: User | null): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  // Compare identity + last-modified so metadata changes (email, avatar) propagate,
  // but routine token refreshes (same id, same updated_at) are suppressed.
  return a.id === b.id && a.updated_at === b.updated_at
}

function syncSentryUser(user: User | null): void {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email })
  } else {
    Sentry.setUser(null)
  }
}

function syncPostHogUser(user: User | null, event?: string): void {
  if (user) {
    posthog.identify(user.id, { email: user.email })
    // Fire signup_completed once — detect new user by created_at within last 5 minutes
    if (event === 'SIGNED_IN') {
      const isNewUser = Date.now() - new Date(user.created_at).getTime() < 5 * 60 * 1000
      if (isNewUser) {
        posthog.capture('signup_completed', { email: user.email })
      }
    }
  } else {
    posthog.reset()
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    void supabase.auth.getSession().then(({ data: { session } }) => {
      setUser((prev) => {
        const next = session?.user ?? null
        if (!isSameUser(prev, next)) {
          syncSentryUser(next)
          syncPostHogUser(next)
        }
        return isSameUser(prev, next) ? prev : next
      })
      setLoading(false)
    })

    // Listen for auth changes — only update state when the user actually changed,
    // not on every Supabase heartbeat / token refresh
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser((prev) => {
        const next = session?.user ?? null
        if (!isSameUser(prev, next)) {
          syncSentryUser(next)
          syncPostHogUser(next, event)
        }
        return isSameUser(prev, next) ? prev : next
      })
    })

    return () => subscription.unsubscribe()
  }, [])

  const getToken = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    return session?.access_token ?? ''
  }

  return {
    user,
    loading,
    getToken,
  }
}
